import type { ButtonHTMLAttributes, HTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { VersionBadge } from './VersionBadge';

/* Primitives the shell's screens need — and no more. Every colour is a semantic token. */

export function Logo({
  className = '',
  compact = false,
}: {
  className?: string;
  compact?: boolean;
}) {
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <span
        aria-hidden="true"
        className="num grid h-[22px] w-[22px] flex-none place-items-center rounded-[5px] bg-accent text-xs font-bold text-ink-on-accent"
      >
        G
      </span>
      {compact ? null : (
        <span className="whitespace-nowrap text-[14.5px] font-semibold tracking-tight">
          GiTrader
        </span>
      )}
    </span>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-panel border border-border bg-surface p-6 shadow-panel ${className}`}>
      {children}
    </div>
  );
}

/** Bordered container with an optional header row; tables live inside `TableScroll`. */
export function Panel({
  children,
  className = '',
  ...rest
}: HTMLAttributes<HTMLElement> & { children: ReactNode; className?: string }) {
  return (
    <section
      className={`overflow-hidden rounded-panel border border-border bg-surface ${className}`}
      {...rest}
    >
      {children}
    </section>
  );
}

export function PanelTitle({
  title,
  sub,
  help,
  children,
}: {
  title: ReactNode;
  sub?: ReactNode;
  /** A `?` beside the title, opening the help panel at this topic id. */
  help?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-border bg-raised px-3.5 py-2.5">
      <h2 className="m-0 flex items-center gap-1.5 text-[13px] font-semibold tracking-tight">
        {title}
        {help}
      </h2>
      {sub ? <span className="text-[11.5px] text-ink-3">{sub}</span> : null}
      {children ? <div className="ml-auto flex items-center gap-2">{children}</div> : null}
    </header>
  );
}

/**
 * The one place a table may become wider than the page: it scrolls here, inside its own
 * container. The page body never scrolls horizontally.
 */
export function TableScroll({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`overflow-x-auto ${className}`} data-table-scroll>
      {children}
    </div>
  );
}

export function Tile({
  label,
  value,
  meta,
  flag = false,
}: {
  label: string;
  value: ReactNode;
  meta?: ReactNode;
  flag?: boolean;
}) {
  return (
    <div
      className={`rounded-panel border border-border bg-surface px-3.5 py-3 ${flag ? 'border-l-[3px] border-l-warn' : ''}`}
    >
      <div className="eyebrow">{label}</div>
      <div className="num mt-1 text-[25px] leading-[1.1] font-semibold tracking-tight">{value}</div>
      {meta ? <div className="mt-0.5 text-[11.5px] text-ink-2">{meta}</div> : null}
    </div>
  );
}

export type TagTone = 'neutral' | 'ok' | 'warn' | 'crit' | 'accent' | 'up' | 'down';

const TAG_TONES: Record<TagTone, string> = {
  neutral: 'border-border bg-raised text-ink-2',
  ok: 'border-transparent bg-ok/15 text-ok',
  warn: 'border-transparent bg-warn/15 text-warn',
  crit: 'border-transparent bg-crit/15 text-crit',
  accent: 'border-transparent bg-accent-soft text-accent-ink',
  up: 'border-transparent bg-up/15 text-up',
  down: 'border-transparent bg-down/15 text-down',
};

export function Tag({
  tone = 'neutral',
  children,
  className = '',
  ...rest
}: HTMLAttributes<HTMLSpanElement> & {
  tone?: TagTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-[7px] py-px text-[10.5px] font-semibold ${TAG_TONES[tone]} ${className}`}
      {...rest}
    >
      {children}
    </span>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="num rounded border border-b-2 border-border-strong bg-raised px-[5px] py-px text-[10.5px] text-ink-2">
      {children}
    </kbd>
  );
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
}

export function Field({ label, hint, id, ...props }: FieldProps) {
  const inputId = id ?? `f-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="eyebrow">
        {label}
      </label>
      <input
        id={inputId}
        className="h-[31px] w-full rounded-panel border border-border-strong bg-surface px-2.5 text-[12.5px] text-ink placeholder:text-ink-3 focus:border-accent focus:outline-none"
        {...props}
      />
      {hint ? <p className="m-0 text-[11.5px] text-ink-3">{hint}</p> : null}
    </div>
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'primary' | 'danger' | 'ghost';
  size?: 'md' | 'sm';
  loading?: boolean;
}

const BUTTON_VARIANTS = {
  default: 'border-border-strong bg-surface text-ink hover:bg-raised',
  primary: 'border-accent bg-accent font-semibold text-ink-on-accent hover:brightness-[1.07]',
  danger: 'border-crit/45 bg-surface text-crit hover:bg-crit/10',
  ghost:
    'border-transparent bg-transparent text-ink-2 hover:border-border hover:bg-sunken hover:text-ink',
};

export function Button({
  variant = 'default',
  size = 'md',
  loading = false,
  children,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  const sizing = size === 'sm' ? 'h-[25px] px-[9px] text-[11.5px]' : 'h-[30px] px-3 text-[12.5px]';
  return (
    <button
      className={`inline-flex items-center justify-center gap-1.5 rounded-panel border font-medium whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${sizing} ${BUTTON_VARIANTS[variant]} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
}

export function IconButton({
  label,
  className = '',
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={`grid h-[30px] w-[30px] flex-none place-items-center rounded-panel border border-transparent text-ink-2 hover:border-border hover:bg-sunken hover:text-ink ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        opacity="0.25"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function Alert({
  kind = 'error',
  children,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  kind?: 'error' | 'warn' | 'info';
  children: ReactNode;
}) {
  const styles = {
    error: 'border-crit/40 bg-crit/10 text-crit',
    warn: 'border-warn/40 bg-warn/10 text-warn',
    info: 'border-accent/40 bg-accent/10 text-accent-ink',
  }[kind];
  return (
    <div role="alert" className={`rounded-panel border px-3 py-2 text-sm ${styles}`} {...props}>
      {children}
    </div>
  );
}

export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <main className="flex min-h-dvh items-center justify-center bg-bg px-4 py-12">
      <div className="w-full max-w-md space-y-6">
        <div className="space-y-2 text-center">
          <Logo className="justify-center" />
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="text-sm text-ink-2">{subtitle}</p>
        </div>
        <Card>{children}</Card>
        <p className="flex items-center justify-center gap-2 text-center text-xs text-ink-3">
          <span>GiTrader · Gimasi SA</span>
          <span aria-hidden="true">·</span>
          <VersionBadge />
        </p>
      </div>
    </main>
  );
}
